import React, { useState, useEffect } from 'react';
import Slideshow from './Slideshow';
import { MarkDownView } from '../../common/MarkDownView';

interface CorrectPairs {
    [key: string]: string;
}

interface KvizPitanjeConnectProps {
    listA: string[]; // Lista gradova
    listB: string[]; // Lista država
    item: any;
    pitanjeIndex: number;
    surveyOdgovori: any;
    setSurveyOdgovori: any;
    showResults?: boolean;
}

const shuffleArray = (array: string[]): string[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

const KvizPitanjeConnect: React.FC<KvizPitanjeConnectProps> = ({
    listA,
    listB,
    item,
    pitanjeIndex,
    surveyOdgovori,
    setSurveyOdgovori,
    showResults = false,
}) => {
    // State za izmešanu listu `listB`
    const [shuffledListB, setShuffledListB] = useState<string[]>([]);
    const [showOpis, setShowOpis] = useState(false);
    const hasOpis = !!item?.opis?.trim();

    // Posle završetka testa opis odgovora se automatski otvara za pitanja koja nisu tačno rešena (ili nisu rešena).
    const osvojenoBodova = surveyOdgovori?.[pitanjeIndex]?.brojBodova ?? 0;
    useEffect(() => {
        if (showResults && osvojenoBodova < 1) setShowOpis(true);
    }, [showResults, osvojenoBodova]);

    if (listA.length !== listB.length) {
        throw new Error("Liste 'listA' i 'listB' moraju biti iste dužine.");
    }

    // Generisanje parova iz prosleđenih lista
    const correctPairs: CorrectPairs = listA.reduce((pairs, city, index) => {
        pairs[city] = listB[index];
        return pairs;
    }, {} as CorrectPairs);

    useEffect(() => {
        // Mešanje liste `listB` kada se komponenta učita
        setShuffledListB(shuffleArray(listB));
    }, [listB]);

    useEffect(() => {
        if (showResults) checkAnswers();
    }, [showResults]);

    // Generisanje boja za svaku stavku
    const [cityColors, setCityColors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        const fixedLightColors = [
            '#E1BEE7', // light purple
            '#FFE0B2', // light orange
            '#B2EBF2', // light cyan
            '#C8E6C9', // light green
            '#C5CAE9', // light indigo
            '#FFF9C4', // light yellow
            '#FFCDD2', // light red
            '#F8BBD0', // light pink
            '#BBDEFB', // light blue
            '#D1C4E9', // light deep purple
        ];

        const colors = listA.reduce(
            (acc, city, index) => {
                acc[city] = fixedLightColors[index % fixedLightColors.length];
                return acc;
            },
            {} as { [key: string]: string },
        );

        setCityColors(colors);
    }, [listA]);

    const [userAnswers, setUserAnswers] = useState<CorrectPairs>({});
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [result, setResult] = useState<string | null>(null);

    // Funkcija za generisanje slučajnih boja
    const generateLightColor = (): string => {
        const min = 180; // Minimalna vrednost za komponente boje (R, G, B) da bi boja bila svetla
        const r = Math.floor(Math.random() * (255 - min) + min);
        const g = Math.floor(Math.random() * (255 - min) + min);
        const b = Math.floor(Math.random() * (255 - min) + min);
        return `rgb(${r}, ${g}, ${b})`;
    };

    const getCountryColor = (country: string): string => {
        const city = Object.keys(userAnswers).find((key) => userAnswers[key] === country);
        return city ? cityColors[city] : '#f5f5f5';
    };

    const getResultsCountryColor = (country: string): string => {
        const city = Object.keys(correctPairs).find((key) => correctPairs[key] === country);
        return city ? cityColors[city] : '#f5f5f5';
    };

    const getCityColor = (city: string): string => {
        const newCity = Object.keys(userAnswers).find((key) => key === city);
        return newCity ? cityColors[city] : '#f5f5f5';
    };

    const getResultsCityColor = (city: string): string => {
        const newCity = Object.keys(correctPairs).find((key) => key === city);
        return newCity ? cityColors[city] : '#f5f5f5';
    };

    const handleSelectCity = (city: string): void => {
        setSelectedCity(city);
    };

    const setSurveyOdgovorMaster = (odgovor: any) => {
        setSurveyOdgovori((prethodnaLista: any[]) => {
            const novaLista = [...prethodnaLista]; // Kopiramo listu
            novaLista[pitanjeIndex] =
                novaLista[pitanjeIndex] === null
                    ? { odgovor, brojBodova: calculateConnectOdgovor(odgovor) } // Ako je null, kreiramo novi objekat
                    : { ...novaLista[pitanjeIndex], odgovor, brojBodova: calculateConnectOdgovor(odgovor) }; // Inače, ažuriramo postojeći objekat
            return novaLista;
        });
    };

    const handleSelectCountry = (country: string): void => {
        if (selectedCity) {
            setUserAnswers((prevAnswers) => {
                const newAnswers = { ...prevAnswers };

                // Uklanjanje prethodnog povezivanja ako država već postoji
                for (const key in newAnswers) {
                    if (newAnswers[key] === country) {
                        delete newAnswers[key];
                    }
                }

                // Dodaj novo povezivanje
                newAnswers[selectedCity] = country;
                setSurveyOdgovorMaster(newAnswers);
                return newAnswers;
            });

            setSelectedCity(null);
        } else {
            alert('Molimo vas da prvo izaberete člana prve liste s kojim želite da uparite element iz druge liste.');
        }
    };

    const checkAnswers = (): void => {
        let correctCount = 0;
        for (let city in correctPairs) {
            if (correctPairs[city] === userAnswers[city]) {
                correctCount++;
            }
        }
        setResult(`Tačno povezanih parova: ${correctCount} od ${listA.length}`);
    };

    const calculateConnectOdgovor = (currentAnswers: any) => {
        let correctCount = 0;
        for (let city in correctPairs) {
            if (correctPairs[city] === currentAnswers[city]) {
                correctCount++;
            }
        }
        let procenatTacnih = correctCount / listA.length;
        return parseFloat(procenatTacnih.toFixed(2));
    };

    const resetGame = (): void => {
        setUserAnswers({});
        setSelectedCity(null);
        setResult(null);
        setSurveyOdgovori((prethodnaLista: any[]) => {
            const novaLista = [...prethodnaLista]; // Kopiramo listu
            novaLista[pitanjeIndex] = null;
            return novaLista;
        });
    };

    return (
        <div className=" flex flex-col pb-3 border-b border-b-blueHoverLight">
            <div className="flex flex-row gap-5 justify-between ">
                <div className="flex-1">
                    <div className="flex flex-col justify-between gap-2 ">
                        <div className="flex flex-row gap-2">
                            <p className="text-[16px] leading-5 text-blueDark font-medium"> {pitanjeIndex + 1}.</p>
                            <MarkDownView source={item.naziv} />
                        </div>
                        {item.oblastImages && item.oblastImages?.length !== 0 && (
                            <div className="block xl:hidden">
                                <Slideshow images={item.oblastImages} width={300} height={200} />
                            </div>
                        )}
                    </div>
                    {!showResults && (
                        <p className="text-[14px] leading-5 text-yellow-900 font-medium mt-2">
                            Povežite pojmove: izaberite stavku sa leve strane, a zatim odgovarajuću stavku sa desne strane
                        </p>
                    )}
                    <div className=" mt-5">
                        <div
                            className="flex flex-col gap-2"
                            style={{
                                flex: 1,
                                maxWidth: 708,
                            }}
                        >
                            {listA.map((itemA, index) => (
                                <div className="grid grid-cols-2 gap-2" key={index}>
                                    {/* Kolona 1 */}
                                    <button
                                        className="w-full px-2 md:px-4 py-2 rounded-[10px] transition "
                                        onClick={() => handleSelectCity(itemA)}
                                        style={{
                                            backgroundColor: getCityColor(itemA),
                                            borderWidth: 2,
                                            borderColor: selectedCity === itemA ? '#3a3a3a' : getCityColor(itemA),
                                        }}
                                        disabled={showResults}
                                    >
                                        <MarkDownView source={itemA} />
                                    </button>

                                    {/* Kolona 2 */}
                                    <button
                                        className="w-full px-2 md:px-4 py-2 rounded-[10px] transition"
                                        onClick={() => handleSelectCountry(shuffledListB[index])}
                                        style={{
                                            backgroundColor: getCountryColor(shuffledListB[index]),
                                            borderWidth: 2,
                                            borderColor: getCountryColor(shuffledListB[index]),
                                        }}
                                        disabled={showResults}
                                    >
                                        <MarkDownView source={shuffledListB[index]} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {!showResults && (
                        <div className="mt-4">
                            <button
                                onClick={resetGame}
                                className="text-[16px] leading-5 text-white bg-[#dc3545] font-medium px-4 py-2 text-left rounded-[10px] transition"
                            >
                                Resetuj odgovore
                            </button>
                        </div>
                    )}
                </div>
                {item.oblastImages && item.oblastImages?.length !== 0 && (
                    <div className="hidden xl:block w-[300px] h-[200px]">
                        <Slideshow images={item.oblastImages} width={300} height={200} />
                    </div>
                )}
            </div>
            {/* Rezultat */}
            {showResults && result && (
                <div className="mt-5 flex flex-col gap-5">
                    <p className="text-[16px] leading-5 text-blueDark font-bold">{result}</p>
                    <p className="text-[16px] leading-5 text-blueDark font-bold">
                        Osvojeno bodova: {(surveyOdgovori[pitanjeIndex] && surveyOdgovori[pitanjeIndex].brojBodova) ?? 0}
                    </p>
                    <p className="text-[16px] leading-5 text-greenA700 font-medium"> Tačan odgovor je:</p>
                </div>
            )}

            {showResults && (
                <div
                    className="flex flex-col gap-2"
                    style={{
                        flex: 1,
                        maxWidth: 708,
                    }}
                >
                    {listA.map((itemA, index) => (
                        <div className="grid grid-cols-2 gap-2" key={index}>
                            {/* Kolona 1 */}
                            <button
                                className="w-full px-2 md:px-4 py-2 rounded-[10px] transition"
                                style={{
                                    backgroundColor: getResultsCityColor(itemA),
                                }}
                                disabled
                            >
                                <MarkDownView source={itemA} />
                                {/* Dodaj slovo ispred grada */}
                                {/* {String.fromCharCode(65 + index)}. {itemA} */}
                                {/* Prikaz rednog broja države ako je grad povezan */}
                                {/* {correctPairs[itemA] ? ` (${shuffledListB.indexOf(correctPairs[itemA]) + 1})` : ''} */}
                            </button>

                            {/* Kolona 2 */}
                            <button
                                className="w-full px-2 md:px-4 py-2 rounded-[10px] transition"
                                style={{
                                    backgroundColor: getResultsCountryColor(shuffledListB[index]),
                                }}
                                disabled
                            >
                                <MarkDownView source={shuffledListB[index]} />
                                {/* Dodaj redni broj ispred države */}
                                {/* {index + 1}. {shuffledListB[index]} */}
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {showResults && hasOpis && (
                <div className="mt-4">
                    <button
                        onClick={() => setShowOpis((prev: boolean) => !prev)}
                        className="text-[16px] leading-5 text-white bg-blueMedium font-medium px-4 py-2 mb-2 text-left rounded-[10px] transition"
                    >
                        {showOpis ? 'Zatvori opis odgovora' : 'Prikaži opis odgovora'}
                    </button>

                    <div
                        className={`bg-orange-50 rounded-lg p-2 ${showOpis ? 'max-h-[1000px] h-auto' : 'max-h-0'} ${showOpis ? 'opacity-100 p-1' : 'opacity-0'} overflow-hidden transition-all`}
                    >
                        <MarkDownView source={item?.opis} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default KvizPitanjeConnect;
